import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/components/ui/tables/Table';
import type { TableColumn } from '@/components/ui/tables/Table';
import { colors } from '@/assets/styles/colors';
import { MonitoringColumnHeader } from '@/components/features/attendance/monitoring/MonitoringColumnHeader';
import type { LC_MonitoringFiltersIn, MonitoringAbsentee } from '@/api/domains/attendance/monitoring';
import {
  useMonitoringAbsentees,
  MONITORING_ABSENTEES_DEFAULT_SORT,
} from '@/hooks/attendance/monitoring/useMonitoringAbsentees';
import { MONITORING_TABLE_MIN_WIDTH } from '@/components/features/attendance/monitoring/monitoringTable.constants';


export interface MonitoringAbsenteesTableProps {
  /** Filtros dimensionales aplicados, emitidos por el panel de filtros compartido del monitoreo. */
  appliedFilters: LC_MonitoringFiltersIn | null;
  /** Token de refresco de la pantalla; cada cambio reconsulta. */
  refreshToken?: number;
}

// Tabla "Ausentes" del Monitoreo de Asistencia: colaboradores activos con contrato vigente que no
// han marcado asistencia hoy o que ya registraron su salida. Tiene su propio buscador, orden por
// columna y paginación —todo resuelto server-side por useMonitoringAbsentees—, independientes
// de las tablas Justificados/Presentes. `serverSideSort` le indica a Table que delegue el orden
// al hook en vez de ordenar la página que recibió.
export const MonitoringAbsenteesTable: React.FC<MonitoringAbsenteesTableProps> = ({
  appliedFilters,
  refreshToken,
}) => {
  const { t: translate } = useTranslation();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
  } = useMonitoringAbsentees(appliedFilters, refreshToken);

  // Se guía por `hasLoadedOnce` y no por las filas: si no, refrescar una lista vacía volvería al
  // skeleton en vez de mantener el estado vacío con el spinner.
  const isInitialLoading = !hasLoadedOnce;
  const isRefreshing = loading && hasLoadedOnce;

  const columns = useMemo<TableColumn<MonitoringAbsentee>[]>(
    () => [
      { key: 'rut', header: translate('attendance:monitoring.absentees.columns.rut'), sortable: true },
      { key: 'fullName', header: translate('attendance:monitoring.absentees.columns.collaborator'), sortable: true },
      // El backend devuelve un código estable ("absent"), no la etiqueta: se traduce acá para no
      // pintar texto en español que venga de la API. No ordenable: el código es constante en todas
      // las filas, así que el backend no lo incluye en su whitelist de orderBy.
      {
        key: 'statusCode',
        header: translate('attendance:monitoring.absentees.columns.status'),
        sortable: false,
        render: (row) => translate(`attendance:monitoring.absentees.status.${row.statusCode}`),
      },
    ],
    [translate],
  );

  return (
    <div className="flex flex-col gap-3" data-testid="monitoring-absentees">
      <MonitoringColumnHeader
        icon="userTimes"
        iconColor={colors.feedback.error300}
        title={translate('attendance:monitoring.absentees.title')}
        isRefreshing={isRefreshing}
        refreshingTestId="monitoring-absentees-refreshing"
      />
      {/* flex-1 + h-full para que esta columna estire a la altura de la fila del grid, igual que
          las tarjetas hermanas de Justificados/Presentes (el grid de la página es items-stretch).
          Ojo: a Table NO se le pasa `emptyMessage` a propósito — su estado vacío por defecto ya
          es el de Figma (ilustración + "Ningún dato para mostrar" + descripción + sugerencia de
          búsqueda), y pasarlo reemplaza el título Y suprime las dos líneas de abajo. */}
      <div
        className={`flex h-full flex-1 transition-opacity duration-200 ${isRefreshing ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={isRefreshing}
      >
        <Table<MonitoringAbsentee>
          className="w-full flex-1"
          minTableWidth={MONITORING_TABLE_MIN_WIDTH}
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isInitialLoading}
          searchable
          searchPlaceholder={translate('attendance:monitoring.absentees.searchPlaceholder')}
          onSearch={onSearch}
          serverSideSort
          initialSort={MONITORING_ABSENTEES_DEFAULT_SORT}
          onSortChange={onSortChange}
          getRowId={(row) => row.rut}
          hideTotalRows={total === 0}
          pagination={
            total > 0
              ? { page, pageSize, total, onChange: setPage, onPageSizeChange: setPageSize }
              : undefined
          }
          data-testid="monitoring-absentees-table"
        />
      </div>
    </div>
  );
};

export default MonitoringAbsenteesTable;
